import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import './AdvancedFilters.css';

export interface FilterOptions {
    dietary: string[];
    allergens: string[];
    categories: string[];
    sortBy: string;
}

interface Category {
    id: number;
    slug: string;
    name: string;
    icon?: string;
}

export interface AdvancedFiltersProps {
    onFilterChange: (filters: FilterOptions) => void;
    categories: Category[];
}

const AdvancedFilters: React.FC<AdvancedFiltersProps> = ({ onFilterChange, categories }) => {
    const { t, i18n } = useTranslation();
    const [filters, setFilters] = useState<FilterOptions>({
        dietary: [],
        allergens: [],
        categories: [],
        sortBy: 'featured'
    });

    // Сброс фильтров категорий при смене языка
    useEffect(() => {
        if (filters.categories.length > 0) {
            // Проверяем, что выбранные категории все еще существуют
            const validCategories = filters.categories.filter(catSlug =>
                categories.some(c => c.slug === catSlug)
            );
            if (validCategories.length !== filters.categories.length) {
                const updatedFilters = { ...filters, categories: validCategories };
                setFilters(updatedFilters);
                onFilterChange(updatedFilters);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [i18n.language, categories]);

    const dietaryOptions = [
        { value: 'vegetarian', label: t('filters.vegetarian'), icon: '🥬' },
        { value: 'vegan', label: t('filters.vegan'), icon: '🌱' },
        { value: 'halal', label: t('filters.halal'), icon: '☪️' },
        { value: 'kosher', label: t('filters.kosher'), icon: '✡️' },
        { value: 'gluten-free', label: t('filters.gluten_free'), icon: '🌾' }
    ];

    const allergenOptions = [
        { value: 'Fish', label: t('filters.fish'), icon: '🐟' },
        { value: 'Milk', label: t('filters.milk'), icon: '🥛' },
        { value: 'Eggs', label: t('filters.eggs'), icon: '🥚' },
        { value: 'Nuts', label: t('filters.nuts'), icon: '🥜' },
        { value: 'Shellfish', label: t('filters.shellfish'), icon: '🦐' },
        { value: 'Soy', label: t('filters.soy'), icon: '🌿' },
        { value: 'Wheat', label: t('filters.wheat'), icon: '🌾' }
    ];

    const sortOptions = [
        { value: 'featured', label: t('filters.featured'), icon: '⭐' },
        { value: 'rating', label: t('filters.highest_rated'), icon: '👍' },
        { value: 'name', label: t('filters.name_az'), icon: '🔤' },
        { value: 'calories', label: t('filters.lowest_calories'), icon: '🔥' }
    ];

    const toggleFilter = (type: keyof FilterOptions, value: string) => {
        const currentFilters = filters[type] as string[];
        const newFilters = currentFilters.includes(value)
            ? currentFilters.filter(item => item !== value)
            : [...currentFilters, value];

        const updatedFilters = { ...filters, [type]: newFilters };
        setFilters(updatedFilters);
        onFilterChange(updatedFilters);
    };

    const updateSort = (value: string) => {
        const updatedFilters = { ...filters, sortBy: value };
        setFilters(updatedFilters);
        onFilterChange(updatedFilters);
    };

    const clearAllFilters = () => {
        const clearedFilters: FilterOptions = {
            dietary: [],
            allergens: [],
            categories: [],
            sortBy: 'featured'
        };
        setFilters(clearedFilters);
        onFilterChange(clearedFilters);
    };

    const activeFiltersCount = filters.dietary.length + filters.allergens.length + filters.categories.length;

    return (
        <div className="advanced-filters-compact">
            {/* Сортировка - всегда видна */}
            {/* <div className="filters-row">
                <div className="filter-group">
                    <span className="filter-label">{t('filters.sort_by')}:</span>
                    <div className="filter-buttons">
                        {sortOptions.map(option => (
                            <button
                                key={option.value}
                                className={`filter-btn ${filters.sortBy === option.value ? 'active' : ''}`}
                                onClick={() => updateSort(option.value)}
                                title={option.label}
                            >
                                {option.icon} {option.label}
                            </button>
                        ))}
                    </div>
                </div>
            </div> */}

            {/* Диета - кнопки */}
            {/* <div className="filters-row">
                <div className="filter-group">
                    <span className="filter-label">{t('filters.dietary_prefs')}:</span>
                    <div className="filter-buttons">
                        {dietaryOptions.map(option => (
                            <button
                                key={option.value}
                                className={`filter-btn ${filters.dietary.includes(option.value) ? 'active' : ''}`}
                                onClick={() => toggleFilter('dietary', option.value)}
                            >
                                {option.icon} {option.label}
                            </button>
                        ))}
                    </div>
                </div>
            </div> */}

            {/* Категории - кнопки */}
            {categories.length > 0 && (
                <div className="filters-row">
                    <div className="filter-group">
                        <span className="filter-label">{t('filters.categories')}:</span>
                        <div className="filter-buttons">
                            {categories.map(category => (
                                <button
                                    key={category.slug}
                                    className={`filter-btn ${filters.categories.includes(category.slug) ? 'active' : ''}`}
                                    onClick={() => toggleFilter('categories', category.slug)}
                                >
                                    {category.icon && `${category.icon} `}{category.name}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Аллергены - кнопки */}
            {/* <div className="filters-row">
                <div className="filter-group">
                    <span className="filter-label">{t('filters.exclude_allergens')}:</span>
                    <div className="filter-buttons">
                        {allergenOptions.map(option => (
                            <button
                                key={option.value}
                                className={`filter-btn allergen-btn ${filters.allergens.includes(option.value) ? 'active' : ''}`}
                                onClick={() => toggleFilter('allergens', option.value)}
                            >
                                {option.icon} {option.label}
                            </button>
                        ))}
                    </div>
                </div>
            </div> */}

            {/* Сброс фильтров */}
            {/* {activeFiltersCount > 0 && (
                <div className="filters-row">
                    <button className="clear-filters-btn" onClick={clearAllFilters}>
                        🔄 {t('filters.clear_all')} ({activeFiltersCount})
                    </button>
                </div>
            )} */}
        </div>
    );
};

export default AdvancedFilters;
